import React, { useEffect, useRef, useState } from 'react';
import './Onboarding.css';
import supabase from '../../supabaseClient';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const languageOptions = [
  { value: 'english', label: 'English' },
  { value: 'spanish', label: 'Spanish' },
  { value: 'french', label: 'French' },
  { value: 'german', label: 'German' },
  { value: 'italian', label: 'Italian' },
  { value: 'portuguese', label: 'Portuguese' },
  { value: 'russian', label: 'Russian' },
  { value: 'japanese', label: 'Japanese' },
  { value: 'chinese', label: 'Chinese' },
  { value: 'korean', label: 'Korean' },
  { value: 'arabic', label: 'Arabic' },
  { value: 'hindi', label: 'Hindi' },
];

const cefrLevels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'Not sure'];

// Removed manual dial code list in favor of react-phone-number-input

const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [existingAvatarUrl, setExistingAvatarUrl] = useState('');
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    name: '',
    display_name: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    location: '',
    occupation: '',
    native_languages: [],
    target_languages: [],
    target_language_levels: {},
    bio: '',
    profilePicture: null,
  });
  // Phone handled as a single E.164 value via PhoneInput
  const [phoneDefaultCountry, setPhoneDefaultCountry] = useState('US');
  // Avatar cropper state
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropMime, setCropMime] = useState('image/jpeg');
  const [cropScale, setCropScale] = useState(1);
  const [cropOffset, setCropOffset] = useState({ x: 0, y: 0 });
  const imgRef = useRef(null);
  const [imgNatural, setImgNatural] = useState({ w: 0, h: 0 });

  useEffect(() => {
    async function loadProfile() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      if (data) {
        setForm(prev => ({
          ...prev,
          ...data,
          native_languages: Array.isArray(data.native_languages) ? data.native_languages.map(v => ({ value: v, label: v })) : [],
          target_languages: Array.isArray(data.target_languages) ? data.target_languages.map(v => ({ value: v, label: v })) : [],
        }));
        if (data.avatar_url) {
          setExistingAvatarUrl(data.avatar_url);
          setPreviewUrl(data.avatar_url);
        }
        if (data.phone) {
          const p = parsePhoneNumberFromString(data.phone);
          if (p?.country) setPhoneDefaultCountry(p.country);
        }
        // Phone will be displayed directly by PhoneInput
      }
    }
    loadProfile();
  }, []);

  const nextStep = () => setStep(s => s + 1);
  const prevStep = () => setStep(s => s - 1);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selected, action) => {
    if (action.name === 'target_languages') {
      // When target languages change, initialize levels for new languages
      const newLangs = (selected || []).map(opt => opt.value || opt);
      const currentLevels = form.target_language_levels || {};
      const newLevels = {};
      newLangs.forEach(lang => {
        newLevels[lang] = currentLevels[lang] || '';
      });
      setForm(prev => ({ ...prev, [action.name]: selected, target_language_levels: newLevels }));
    } else {
      setForm(prev => ({ ...prev, [action.name]: selected }));
    }
  };

  const handleLevelChange = (language, level) => {
    setForm(prev => ({
      ...prev,
      target_language_levels: {
        ...(prev.target_language_levels || {}),
        [language]: level
      }
    }));
  };

  const MIN_AVATAR_SIZE = 256;
  const TARGET_AVATAR_SIZE = 512; // recommended output size
  const MAX_UPLOAD_SIZE = 5 * 1024 * 1024; // 5MB

  // Center-crop to square and resize to TARGET_AVATAR_SIZE
  const processAvatar = async (dataUrl, mime) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const { width, height } = img;
        const side = Math.min(width, height);
        const sx = Math.floor((width - side) / 2);
        const sy = Math.floor((height - side) / 2);
        const canvas = document.createElement('canvas');
        canvas.width = TARGET_AVATAR_SIZE;
        canvas.height = TARGET_AVATAR_SIZE;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, sx, sy, side, side, 0, 0, TARGET_AVATAR_SIZE, TARGET_AVATAR_SIZE);
        const outType = mime === 'image/png' ? 'image/png' : 'image/jpeg';
        const quality = outType === 'image/jpeg' ? 0.9 : 1.0;
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Failed to process image.'));
            return;
          }
          resolve(blob);
        }, outType, quality);
      };
      img.onerror = () => reject(new Error('Unable to process image.'));
      img.src = dataUrl;
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Quick MIME check (optional)
    if (!file.type.startsWith('image/')) {
      setError('Please upload a valid image file.');
      return;
    }
    // Reject extremely large files early (e.g., > 15MB) to avoid memory spikes
    if (file.size > 15 * 1024 * 1024) {
      setError('File too large. Please upload an image smaller than 15MB.');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const img = new Image();
        img.onload = async () => {
          const { width, height } = img;
          if (width < MIN_AVATAR_SIZE || height < MIN_AVATAR_SIZE) {
            setError(`Profile picture must be at least ${MIN_AVATAR_SIZE}x${MIN_AVATAR_SIZE}px.`);
            setForm(prev => ({ ...prev, profilePicture: null }));
            setPreviewUrl(null);
            return;
          }
          // Open cropper with initial values
          setCropSrc(reader.result);
          setCropMime(file.type);
          setCropOpen(true);
          setError('');
        };
        img.onerror = () => setError('Unable to read image. Please try another file.');
        img.src = reader.result;
      } catch (err) {
        setError(err.message || 'Failed to process image.');
      }
    };
    reader.readAsDataURL(file);
  };

  const triggerFileInput = () => fileInputRef.current?.click();

  // Validations
  const validStep1 = () => !!(form.name && form.display_name && form.phone && isValidPhoneNumber(form.phone));
  const isDOBValid = () => {
    if (!form.date_of_birth) return false;
    const dob = new Date(form.date_of_birth);
    if (isNaN(dob.getTime())) return false;
    const today = new Date();
    if (dob > today) return false;
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    return age >= 13;
  };
  const validStep2 = () => form.gender && form.location && form.occupation && isDOBValid();
  const validStep3 = () => {
    if ((form.native_languages?.length || 0) === 0) return false;
    if ((form.target_languages?.length || 0) === 0) return false;
    if (!form.bio) return false;
    // Check that each target language has a level selected
    const targetLangs = (form.target_languages || []).map(opt => opt.value || opt);
    const levels = form.target_language_levels || {};
    return targetLangs.every(lang => levels[lang] && levels[lang].trim() !== '');
  };
  const validStep4 = () => !!(form.profilePicture || existingAvatarUrl);

  const submitProfile = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Derive phone_country_code from the E.164 phone
      let phoneCountryCode = null;
      if (form.phone) {
        const p = parsePhoneNumberFromString(form.phone);
        if (p) phoneCountryCode = `+${p.countryCallingCode}`;
      }

      const payload = {
        id: user.id,
        name: form.name,
        display_name: form.display_name,
        phone: form.phone,
        phone_country_code: phoneCountryCode,
        date_of_birth: form.date_of_birth || null,
        gender: form.gender,
        location: form.location,
        occupation: form.occupation,
        native_languages: (form.native_languages || []).map(o => o.value || o),
        target_languages: (form.target_languages || []).map(o => o.value || o),
        target_language_levels: form.target_language_levels || {},
        bio: form.bio,
        updated_at: new Date().toISOString(),
      };

      const { error: upsertError } = await supabase
        .from('profiles')
        .upsert(payload, { onConflict: 'id' });
      if (upsertError) throw upsertError;

      // Upload avatar if provided
      if (form.profilePicture) {
        const mime = form.profilePicture.type || 'image/jpeg';
        const fileExt = mime === 'image/png' ? 'png' : 'jpg';
        const fileName = `${Math.random().toString(36).slice(2)}.${fileExt}`;
        const filePath = `${user.id}/${fileName}`;
        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(filePath, form.profilePicture);
        if (!uploadError) {
          const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(filePath);
          if (urlData?.publicUrl) {
            await supabase
              .from('profiles')
              .update({ avatar_url: urlData.publicUrl })
              .eq('id', user.id);
            setExistingAvatarUrl(urlData.publicUrl);
          }
        } else {
          throw uploadError;
        }
      }
      // Enforce that an avatar exists after operation
      if (!form.profilePicture && !existingAvatarUrl) {
        throw new Error('A profile picture is required. Please upload one to continue.');
      }

      navigate('/app/home', { replace: true });
    } catch (err) {
      setError(err.message || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        <h2>Complete Your Profile</h2>
        <p className="subtitle">Finish setting up your account so we can personalize your experience.</p>
        {error && <div className="error-banner">{error}</div>}

        <div className="steps-container">
          <div className="steps">
            <div className={`step ${step === 1 ? 'active' : ''}`}>1</div>
            <div className={`step ${step === 2 ? 'active' : ''}`}>2</div>
            <div className={`step ${step === 3 ? 'active' : ''}`}>3</div>
            <div className={`step ${step === 4 ? 'active' : ''}`}>4</div>
          </div>
        </div>

        {/* Step 1: Basic information */}
        {step === 1 && (
          <form onSubmit={(e) => {
            e.preventDefault();
            if (!validStep1()) { setError('Please enter a valid phone number.'); return; }
            nextStep();
          }}>
            <div className="grid-2">
              <div className="form-group">
                <label htmlFor="name">Full Name</label>
                <input id="name" name="name" value={form.name} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="display_name">Display Name</label>
                <input id="display_name" name="display_name" value={form.display_name} onChange={handleChange} required />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <PhoneInput
                international
                defaultCountry={phoneDefaultCountry}
                value={form.phone || ''}
                onChange={(value) => setForm(prev => ({ ...prev, phone: value || '' }))}
                id="phone"
                name="phone"
              />
            </div>
            <div className="actions between">
              <button type="button" className="secondary" disabled title="Onboarding information is required to continue">Skip for now</button>
              <button type="submit" className="primary" disabled={!validStep1()}>Continue</button>
            </div>
          </form>
        )}

        {/* Step 2: Personal details */}
        {step === 2 && (
          <form onSubmit={(e) => { e.preventDefault(); if (validStep2()) nextStep(); }}>
            <div className="grid-2">
              <div className="form-group">
                <label htmlFor="date_of_birth">Date of Birth</label>
                <input id="date_of_birth" name="date_of_birth" type="date" value={form.date_of_birth || ''} onChange={handleChange} required />
                {form.date_of_birth && !isDOBValid() && (
                  <small className="field-error">DOB must not be in the future and age must be at least 13.</small>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="gender">Gender</label>
                <select id="gender" name="gender" value={form.gender || ''} onChange={handleChange} required>
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="non-binary">Non-binary</option>
                  <option value="prefer-not-to-say">Prefer not to say</option>
                </select>
              </div>
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label htmlFor="location">Location</label>
                <input id="location" name="location" value={form.location || ''} onChange={handleChange} placeholder="City, Country" required />
              </div>
              <div className="form-group">
                <label htmlFor="occupation">Occupation</label>
                <input id="occupation" name="occupation" value={form.occupation || ''} onChange={handleChange} required />
              </div>
            </div>
            <div className="actions between">
              <button type="button" className="secondary" onClick={prevStep}>Back</button>
              <button type="submit" className="primary" disabled={!validStep2()}>Continue</button>
            </div>
          </form>
        )}

        {/* Step 3: Language preferences */}
        {step === 3 && (
          <form onSubmit={(e) => { e.preventDefault(); if (validStep3()) nextStep(); }}>
            <div className="form-group">
              <label>Language(s) You Speak</label>
              <Select
                isMulti
                name="native_languages"
                options={languageOptions}
                className="react-select-container"
                classNamePrefix="react-select"
                value={form.native_languages}
                onChange={(selected) => handleSelectChange(selected, { name: 'native_languages' })}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }), menu: base => ({ ...base, zIndex: 9999 }) }}
                menuPosition="fixed"
                placeholder="Select languages you speak fluently"
              />
            </div>
            <div className="form-group">
              <label>Language(s) You Want to Learn</label>
              <Select
                isMulti
                name="target_languages"
                options={languageOptions}
                className="react-select-container"
                classNamePrefix="react-select"
                value={form.target_languages}
                onChange={(selected) => handleSelectChange(selected, { name: 'target_languages' })}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }), menu: base => ({ ...base, zIndex: 9999 }) }}
                menuPosition="fixed"
                placeholder="Select languages you want to learn"
              />
            </div>
            {(form.target_languages || []).length > 0 && (
              <div className="form-group">
                <label>Your Level in Each Language</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {(form.target_languages || []).map((langOpt) => {
                    const lang = langOpt.value || langOpt;
                    const label = langOpt.label || langOpt;
                    return (
                      <div key={lang} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ minWidth: '120px', fontWeight: '500' }}>{label}:</span>
                        <select
                          value={(form.target_language_levels || {})[lang] || ''}
                          onChange={(e) => handleLevelChange(lang, e.target.value)}
                          style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                          required
                        >
                          <option value="">Select level</option>
                          {cefrLevels.map(lvl => (
                            <option key={lvl} value={lvl}>{lvl}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="form-group">
              <label htmlFor="bio">Bio</label>
              <textarea id="bio" name="bio" rows={4} value={form.bio || ''} onChange={handleChange} placeholder="Tell us about yourself..." required />
            </div>
            <div className="actions between">
              <button type="button" className="secondary" onClick={prevStep}>Back</button>
              <button type="submit" className="primary" disabled={!validStep3()}>Continue</button>
            </div>
          </form>
        )}

        {step === 4 && (
          <form onSubmit={(e) => { e.preventDefault(); if (validStep4()) submitProfile(); }}>
            <div className="form-group">
              <label>Profile Picture (square, at least {MIN_AVATAR_SIZE}x{MIN_AVATAR_SIZE}px)</label>
              <div className="profile-picture-container">
                {previewUrl ? (
                  <div className="profile-preview">
                    <img src={previewUrl} alt="Profile preview" />
                    <button type="button" className="change-picture-button" onClick={triggerFileInput}>Change Picture</button>
                  </div>
                ) : (
                  <div className="profile-upload" onClick={triggerFileInput}>
                    <div className="upload-placeholder">
                      <i className="upload-icon">📷</i>
                      <p>Click to upload your profile picture</p>
                    </div>
                  </div>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
              </div>
              <small className="hint">Recommended: square 512×512, under 5 MB. You can crop and zoom after selecting.</small>
            </div>
            <div className="actions between">
              <button type="button" className="secondary" onClick={prevStep}>Back</button>
              <button type="submit" className="primary" disabled={loading || !validStep4()}>
                {loading ? 'Saving...' : 'Finish Onboarding'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Simple Cropper Modal */}
      {cropOpen && (
        <div
          className="cropper-overlay"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000
          }}
          onClick={() => setCropOpen(false)}
        >
          <div
            className="cropper-modal"
            style={{ background: 'var(--surface, #141827)', padding: 16, borderRadius: 12, width: 420 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Adjust your photo</h3>
            <div
              className="crop-viewport"
              style={{ width: 320, height: 320, margin: '0 auto', borderRadius: 12, overflow: 'hidden', position: 'relative', background: '#111' }}
              onMouseDown={(ev) => {
                const start = { x: ev.clientX, y: ev.clientY };
                const orig = { ...cropOffset };
                const onMove = (mv) => {
                  setCropOffset({ x: orig.x + (mv.clientX - start.x), y: orig.y + (mv.clientY - start.y) });
                };
                const onUp = () => {
                  window.removeEventListener('mousemove', onMove);
                  window.removeEventListener('mouseup', onUp);
                };
                window.addEventListener('mousemove', onMove);
                window.addEventListener('mouseup', onUp);
              }}
            >
              {cropSrc && (
                <img
                  ref={imgRef}
                  src={cropSrc}
                  alt="to crop"
                  onLoad={(e) => {
                    const el = e.currentTarget;
                    setImgNatural({ w: el.naturalWidth, h: el.naturalHeight });
                    // Fit to cover at scale=1
                    const base = 320 / Math.min(el.naturalWidth, el.naturalHeight);
                    // Ensure image fully covers viewport at scale 1
                    setCropScale(1);
                    setCropOffset({ x: 0, y: 0 });
                  }}
                  style={{
                    position: 'absolute', left: '50%', top: '50%',
                    transform: `translate(calc(-50% + ${cropOffset.x}px), calc(-50% + ${cropOffset.y}px)) scale(${cropScale})`,
                    transformOrigin: 'center center',
                    userSelect: 'none', pointerEvents: 'none'
                  }}
                />
              )}
            </div>
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
              <label style={{ minWidth: 60 }}>Zoom</label>
              <input
                type="range" min={1} max={3} step={0.01}
                value={cropScale}
                onChange={(e) => setCropScale(parseFloat(e.target.value))}
                style={{ flex: 1 }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button type="button" className="secondary" onClick={() => setCropOpen(false)}>Cancel</button>
              <button
                type="button"
                className="primary"
                onClick={async () => {
                  try {
                    const img = imgRef.current;
                    if (!img) return;
                    const naturalW = imgNatural.w;
                    const naturalH = imgNatural.h;
                    // Base scale fits smaller side to 320 at scale=1
                    const baseScale = 320 / Math.min(naturalW, naturalH);
                    const displayScale = baseScale * cropScale;
                    const srcSide = 320 / displayScale; // size in image pixels
                    // Center point in image pixels then apply offset
                    let sX = naturalW / 2 - srcSide / 2 - (cropOffset.x / displayScale);
                    let sY = naturalH / 2 - srcSide / 2 - (cropOffset.y / displayScale);
                    // Clamp to image bounds
                    sX = Math.max(0, Math.min(naturalW - srcSide, sX));
                    sY = Math.max(0, Math.min(naturalH - srcSide, sY));

                    const canvas = document.createElement('canvas');
                    canvas.width = TARGET_AVATAR_SIZE;
                    canvas.height = TARGET_AVATAR_SIZE;
                    const ctx = canvas.getContext('2d');
                    const tmpImg = new Image();
                    tmpImg.onload = () => {
                      ctx.drawImage(tmpImg, sX, sY, srcSide, srcSide, 0, 0, TARGET_AVATAR_SIZE, TARGET_AVATAR_SIZE);
                      const outType = cropMime === 'image/png' ? 'image/png' : 'image/jpeg';
                      const quality = outType === 'image/jpeg' ? 0.9 : 1.0;
                      canvas.toBlob((blob) => {
                        if (!blob) {
                          setError('Failed to process image.');
                          return;
                        }
                        if (blob.size > MAX_UPLOAD_SIZE) {
                          setError('Processed image is too large. Please choose a smaller image.');
                          return;
                        }
                        setForm(prev => ({ ...prev, profilePicture: blob }));
                        setPreviewUrl(URL.createObjectURL(blob));
                        setCropOpen(false);
                      }, outType, quality);
                    };
                    tmpImg.src = cropSrc;
                  } catch (err) {
                    setError(err.message || 'Failed to crop image.');
                  }
                }}
              >
                Use Photo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Onboarding;
